import { addPageToSessionStorage } from "@utils/helpers";

interface Page {
  page: number;
  numOfPages: number;
  changePage: (arg: number | string) => void;
  setPage: (arg: number) => void;
}

const Pagination: any = (prop: Page) => {
  const { page, numOfPages, setPage } = prop;

  const changePage = (newPage: number) => {
    setPage(newPage);
    addPageToSessionStorage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addPageButton = ({
    pageNumber,
    activeClass,
  }: {
    pageNumber: number;
    activeClass: boolean;
  }) => {
    return (
      <li
        className={`page-item ${activeClass ? "active" : ""}`}
        key={pageNumber}
      >
        <button
          className="page-link"
          onClick={() => changePage(pageNumber)}
          style={{
            minWidth: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            margin: "0 4px",
            border: activeClass ? "none" : "1px solid #dee2e6",
            backgroundColor: activeClass ? "#47BBA9" : "white",
            color: activeClass ? "white" : "#0A0F1E",
            fontWeight: activeClass ? "600" : "400",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            if (!activeClass) {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
              e.currentTarget.style.borderColor = "#47BBA9";
            }
          }}
          onMouseLeave={(e) => {
            if (!activeClass) {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.borderColor = "#dee2e6";
            }
          }}
        >
          {pageNumber}
        </button>
      </li>
    );
  };

  const handleNext = () => {
    const newPage = page + 1;
    changePage(newPage > numOfPages ? 1 : newPage);
  };

  const handlePrev = () => {
    const newPage = page - 1;
    changePage(newPage < 1 ? numOfPages : newPage);
  };

  const renderPageButtons = () => {
    const pageButtons: any = [];
    const showPages = 3;

    if (numOfPages <= showPages * 2 + 1) {
      for (let i = 1; i <= numOfPages; i++) {
        pageButtons.push(
          addPageButton({ pageNumber: i, activeClass: page === i }),
        );
      }
    } else {
      pageButtons.push(
        addPageButton({ pageNumber: 1, activeClass: page === 1 }),
      );
      pageButtons.push(
        addPageButton({ pageNumber: 2, activeClass: page === 2 }),
      );

      if (page > showPages + 2) {
        pageButtons.push(
          <li className="page-item" key="dots-1">
            <span
              className="page-link"
              style={{
                minWidth: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                margin: "0 4px",
                border: "none",
                backgroundColor: "transparent",
                color: "#6c757d",
              }}
            >
              ...
            </span>
          </li>,
        );
      }

      const start = Math.max(3, page - 1);
      const end = Math.min(numOfPages - 2, page + 1);

      for (let i = start; i <= end; i++) {
        pageButtons.push(
          addPageButton({ pageNumber: i, activeClass: page === i }),
        );
      }

      if (page < numOfPages - showPages - 1) {
        pageButtons.push(
          <li className="page-item" key="dots-2">
            <span
              className="page-link"
              style={{
                minWidth: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                margin: "0 4px",
                border: "none",
                backgroundColor: "transparent",
                color: "#6c757d",
              }}
            >
              ...
            </span>
          </li>,
        );
      }

      pageButtons.push(
        addPageButton({
          pageNumber: numOfPages - 1,
          activeClass: page === numOfPages - 1,
        }),
      );
      pageButtons.push(
        addPageButton({
          pageNumber: numOfPages,
          activeClass: page === numOfPages,
        }),
      );
    }

    return pageButtons;
  };

  if (numOfPages < 1) return null;

  // Always show pagination when there's data (at least 1 page)
  if (numOfPages === 1) {
    return (
      <nav
        aria-label="Page navigation"
        className="d-flex justify-content-end my-1 mobile-pagination"
      >
        <ul
          className="pagination"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            listStyle: "none",
            padding: 0,
            margin: 0,
          }}
        >
          <li className="page-item">
            <button
              style={{
                padding: "8px 20px",
                borderRadius: "8px",
                border: "1px solid #dee2e6",
                backgroundColor: "white",
                color: "#0A0F1E",
                fontWeight: "500",
                opacity: 0.5,
                cursor: "not-allowed",
              }}
              disabled
            >
              <i className="fe fe-arrow-left"></i>
              <span className="ms-2">Previous</span>
            </button>
          </li>
          <li className="page-item">
            <button
              className="page-link"
              style={{
                minWidth: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                margin: "0 4px",
                border: "none",
                backgroundColor: "#47BBA9",
                color: "white",
                fontWeight: "600",
              }}
            >
              1
            </button>
          </li>
          <li className="page-item">
            <button
              style={{
                padding: "8px 20px",
                borderRadius: "8px",
                border: "1px solid #dee2e6",
                backgroundColor: "white",
                color: "#0A0F1E",
                fontWeight: "500",
                opacity: 0.5,
                cursor: "not-allowed",
              }}
              disabled
            >
              <span className="me-2">Next</span>
              <i className="fe fe-arrow-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    );
  }

  const navButtonStyle = {
    padding: "8px 20px",
    borderRadius: "8px",
    border: "1px solid #dee2e6",
    backgroundColor: "white",
    color: "#0A0F1E",
    fontWeight: "500",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  return (
    <nav
      aria-label="Page navigation"
      className="d-flex justify-content-end mobile-pagination"
    >
      <ul
        className="pagination"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          listStyle: "none",
          padding: 0,
          margin: 0,
        }}
      >
        <li className="page-item">
          <button
            style={{
              ...navButtonStyle,
              opacity: page === 1 ? 0.5 : 1,
              cursor: page === 1 ? "not-allowed" : "pointer",
            }}
            onClick={handlePrev}
            disabled={page === 1}
            onMouseEnter={(e) => {
              if (page !== 1) {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
                e.currentTarget.style.borderColor = "#47BBA9";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.borderColor = "#dee2e6";
            }}
          >
            <i className="fe fe-arrow-left"></i>
            <span>Previous</span>
          </button>
        </li>

        {renderPageButtons()}

        <li className="page-item">
          <button
            style={{
              ...navButtonStyle,
              opacity: page === numOfPages ? 0.5 : 1,
              cursor: page === numOfPages ? "not-allowed" : "pointer",
            }}
            onClick={handleNext}
            disabled={page === numOfPages}
            onMouseEnter={(e) => {
              if (page !== numOfPages) {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
                e.currentTarget.style.borderColor = "#47BBA9";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.borderColor = "#dee2e6";
            }}
          >
            <span>Next</span>
            <i className="fe fe-arrow-right"></i>
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
